import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Clock, Play, Square, Coffee, StopCircle } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { month: "short", day: "numeric" });
};

const calcBreakMins = (breaks = []) =>
  breaks.filter((b) => b.start && b.end)
    .reduce((a, b) => a + (new Date(b.end) - new Date(b.start)) / 60000, 0);

const calcLiveHours = (clockIn, breaks) => {
  if (!clockIn) return 0;
  const total = (Date.now() - new Date(clockIn).getTime()) / 60000;
  return Math.max(0, (total - calcBreakMins(breaks)) / 60).toFixed(2);
};

export default function TimeTracking() {
  const [attendance, setAttendance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [liveHours, setLiveHours] = useState(0);
  const [time, setTime] = useState(new Date());

  const fetchData = useCallback(() => {
    Promise.all([
      api.get("/attendance/today").catch(() => ({ data: null })),
      api.get("/attendance/my"),
    ]).then(([today, hist]) => {
      setAttendance(today.data);
      setHistory(hist.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (attendance?.clock_in && !attendance?.clock_out) {
      setLiveHours(calcLiveHours(attendance.clock_in, attendance.breaks));
      const t = setInterval(() => {
        setLiveHours(calcLiveHours(attendance.clock_in, attendance.breaks));
      }, 30000);
      return () => clearInterval(t);
    }
  }, [attendance]);

  const doAction = async (action) => {
    setActionLoading(action);
    try {
      const endpoints = {
        "clock-in": "/attendance/clock-in",
        "clock-out": "/attendance/clock-out",
        "break-start": "/attendance/break/start",
        "break-end": "/attendance/break/end",
      };
      const r = await api.post(endpoints[action]);
      setAttendance(r.data);
      toast.success({
        "clock-in": "Clocked in successfully!",
        "clock-out": "Clocked out. Great work today!",
        "break-start": "Break started.",
        "break-end": "Break ended. Welcome back!",
      }[action]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading("");
    }
  };

  const isClockedIn = !!attendance?.clock_in && !attendance?.clock_out;
  const isClockedOut = !!attendance?.clock_out;
  const onBreak = attendance?.breaks?.some((b) => !b.end);
  const hasClockIn = !!attendance?.clock_in;

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-slate-900">Time Tracking</h1>
          <p className="text-slate-500 text-sm mt-1">Track your daily work hours</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Clock */}
          <div className="lg:col-span-1 bg-[#1a2332] rounded-2xl p-7 text-white flex flex-col items-center justify-center text-center">
            <div className="p-4 bg-[#38b6e8]/10 rounded-full mb-4">
              <Clock size={32} className="text-[#38b6e8]" />
            </div>
            <p className="text-4xl font-bold tracking-tight font-mono" data-testid="live-clock">
              {time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
            </p>
            <p className="text-slate-400 text-sm mt-2">
              {time.toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" })}
            </p>
            {isClockedIn && (
              <div className="mt-4 bg-[#38b6e8]/10 rounded-lg px-4 py-2">
                <p className="text-[#38b6e8] text-sm font-semibold">{liveHours}h active today</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
            <h3 className="font-semibold text-slate-800 mb-5">Attendance Actions</h3>

            {loading ? (
              <div className="h-32 animate-pulse bg-slate-100 rounded-xl" />
            ) : isClockedOut ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-5 py-3 rounded-full text-sm font-semibold">
                  <Square size={16} /> Day complete — {attendance.active_hours}h worked
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Clock In */}
                <button
                  onClick={() => doAction("clock-in")}
                  disabled={!!actionLoading || hasClockIn}
                  className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all font-semibold text-sm
                    ${hasClockIn
                      ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60"
                      : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 hover:shadow-sm active:scale-95"
                    }`}
                  data-testid="clock-in-btn"
                >
                  <div className={`p-3 rounded-full ${hasClockIn ? "bg-slate-200" : "bg-emerald-200"}`}>
                    <Play size={22} fill="currentColor" />
                  </div>
                  {actionLoading === "clock-in" ? "Clocking In..." : "Clock In"}
                </button>

                {/* Break */}
                <button
                  onClick={() => doAction(onBreak ? "break-end" : "break-start")}
                  disabled={!!actionLoading || !isClockedIn}
                  className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all font-semibold text-sm
                    ${!isClockedIn
                      ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60"
                      : onBreak
                        ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-300 hover:shadow-sm active:scale-95"
                        : "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100 hover:border-sky-300 hover:shadow-sm active:scale-95"
                    }`}
                  data-testid="break-btn"
                >
                  <div className={`p-3 rounded-full ${!isClockedIn ? "bg-slate-200" : onBreak ? "bg-amber-200" : "bg-sky-200"}`}>
                    <Coffee size={22} />
                  </div>
                  {actionLoading === "break-start" || actionLoading === "break-end"
                    ? "Processing..."
                    : onBreak ? "End Break" : "Start Break"}
                </button>

                {/* Clock Out */}
                <button
                  onClick={() => doAction("clock-out")}
                  disabled={!!actionLoading || !isClockedIn}
                  className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all font-semibold text-sm
                    ${!isClockedIn
                      ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60"
                      : "bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300 hover:shadow-sm active:scale-95"
                    }`}
                  data-testid="clock-out-btn"
                >
                  <div className={`p-3 rounded-full ${!isClockedIn ? "bg-slate-200" : "bg-red-200"}`}>
                    <StopCircle size={22} />
                  </div>
                  {actionLoading === "clock-out" ? "Clocking Out..." : "Clock Out"}
                </button>
              </div>
            )}

            {/* Today summary */}
            {attendance && (
              <div className="mt-6 pt-5 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
                {[
                  { label: "Clock In", val: fmt(attendance.clock_in) },
                  { label: "Break Time", val: calcBreakMins(attendance.breaks) > 0 ? `${Math.round(calcBreakMins(attendance.breaks))}m` : "—" },
                  { label: "Active Hours", val: attendance.active_hours > 0 ? `${attendance.active_hours}h` : isClockedIn ? `${liveHours}h` : "—" },
                ].map(({ label, val }) => (
                  <div key={label}>
                    <p className="text-slate-400 text-xs mb-1">{label}</p>
                    <p className="font-bold text-slate-800 text-sm" data-testid={`today-${label.toLowerCase().replace(/\s+/g, "-")}`}>{val}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* History */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Attendance History</h3>
          </div>
          {history.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">No attendance records yet</div>
          ) : (
            <table className="w-full" data-testid="attendance-history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Date", "Clock In", "Clock Out", "Breaks", "Active Hours", "Status"].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700">{r.date}</td>
                    <td className="px-5 py-3.5 text-sm text-emerald-600 font-medium">{fmt(r.clock_in)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">{fmt(r.clock_out)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">
                      {calcBreakMins(r.breaks) > 0 ? `${Math.round(calcBreakMins(r.breaks))}m` : "—"}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="font-bold text-[#1a2332] text-sm">{r.active_hours > 0 ? `${r.active_hours}h` : "—"}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        r.clock_out ? "bg-emerald-100 text-emerald-700" : r.clock_in ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
